import { z } from 'zod'

/**
 * Cross-cutting contract primitives. Every timestamp crossing the API boundary is UTC ISO-8601
 * (`2026-09-30T01:02:03Z`); display conversion to the device timezone happens in shared/i18n.
 */
export const utcTimestampSchema = z.iso.datetime()
export type UtcTimestamp = z.infer<typeof utcTimestampSchema>

/** Direction of a sorted list column (dataset list, model list). */
export const sortOrderSchema = z.enum(['asc', 'desc'])
export type SortOrder = z.infer<typeof sortOrderSchema>

/** A specific machine placed in the asset tree, e.g. `PRESS-4000-01`. */
export const deviceIdSchema = z.string().min(1).brand<'DeviceId'>()
export type DeviceId = z.infer<typeof deviceIdSchema>

/** Machines sharing construction and failure modes, e.g. `press_4000t`. */
export const deviceTypeSchema = z.string().min(1).brand<'DeviceType'>()
export type DeviceType = z.infer<typeof deviceTypeSchema>

/**
 * Standardized monitoring capability shared across device types. Open-ended on purpose: new
 * device types may introduce capabilities without a frontend release.
 */
export const KNOWN_DEVICE_CAPABILITIES = [
  'vibration',
  'motor_current',
  'temperature',
  'cycle_count',
  'pressure',
] as const
export const deviceCapabilitySchema = z.string().min(1).brand<'DeviceCapability'>()
export type DeviceCapability = z.infer<typeof deviceCapabilitySchema>

export const LIFECYCLE_STATES = [
  'draft',
  'commissioning',
  'shadow',
  'active',
  'maintenance',
  'retired',
] as const
export const lifecycleStateSchema = z.enum(LIFECYCLE_STATES)
export type LifecycleState = z.infer<typeof lifecycleStateSchema>

export const SEVERITIES = ['critical', 'warning', 'info', 'ok'] as const
export const severitySchema = z.enum(SEVERITIES)
export type Severity = z.infer<typeof severitySchema>

/** Asset tree level used for navigation, reporting and RBAC scope. */
export const assetLevelSchema = z.enum(['global', 'plant', 'shop', 'line', 'station', 'device'])
export const assetScopeSchema = z.object({
  level: assetLevelSchema,
  id: z.string().min(1).optional(),
})
export type AssetScope = z.infer<typeof assetScopeSchema>

/**
 * Error bodies of the backend: FastAPI's `{ "detail": "..." }` (or a validation error list), and
 * `{ "code": "...", "message": "..." }` where `code` is stable and `message` is for a person.
 */
export const apiErrorBodySchema = z.union([
  z.object({
    detail: z.union([
      z.string(),
      z.array(
        z.object({
          loc: z.array(z.union([z.string(), z.number()])),
          msg: z.string(),
          type: z.string(),
        }),
      ),
    ]),
  }),
  z.object({
    code: z.string().min(1),
    message: z.string(),
  }),
])
export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>
